import { ActionButton, CompanyLogo as Logo } from '../parts/index.js';
import { useEffect, useState } from 'react';
import { dayMonth } from '../day.js';
import { moneyFull } from './heat-words.js';
import { closeApi, type ClosedDeal, type ClosedSummary } from './close-api.js';
import { REASONS } from './CloseBox.js';

const monthWords = (m: string, short = false) => new Date(`${m}-01T12:00:00Z`).toLocaleDateString('en-GB', { month: short ? 'short' : 'long', ...(short ? {} : { year: 'numeric' }), timeZone: 'UTC' });
export const CLOSED_EMPTY = "The graph counts deals won and lost in Cobrain from 12 Oct 2026. The 87 won before then, seeded from HubSpot, are on each company's record.";

export function ClosedOutcome({ deal, open }: { deal: ClosedDeal; open: (path: string) => void }) {
  const overdue = !!deal.contractDueAt && Date.parse(deal.contractDueAt) <= Date.now();
  return <span className="dc-outcome">
    <b className={deal.outcome}>{deal.outcome === 'won' ? 'Won' : 'Lost'}</b>
    {deal.contractToCome ? <small>{overdue ? 'Contract to come, 30 days.' : 'Contract to come'}</small> : null}
    {overdue ? <small><ActionButton reading type="button" className="hx-link" onClick={() => open(`/deals/${encodeURIComponent(deal.id)}`)}>File the copy</ActionButton></small> : null}
  </span>;
}
function ClosedRow({ deal, store, open, changed }: { deal: ClosedDeal; store: string; open: (path: string) => void; changed: () => void }) {
  const [busy, setBusy] = useState(false);
  const [wrong, setWrong] = useState<string | null>(null);
  const reason = deal.lostReason === 'other' ? deal.lostNote : REASONS.find((x) => x.reason === deal.lostReason)?.words.toLowerCase();
  return <div className="dc-row">
    <span><span className="co-with-logo">{deal.companyName !== null ? <Logo name={deal.companyName} logo={deal.logo} size="sm" /> : null}
      <ActionButton reading type="button" className="hx-link" onClick={() => open(`/deals/${encodeURIComponent(deal.id)}`)}>{deal.companyName ?? deal.product}</ActionButton></span>
      <small>{[deal.product, deal.outcome === 'lost' ? reason : null, deal.closedBy, dayMonth(new Date(deal.closedAt))].filter(Boolean).join(', ')}</small>
    </span>
    <ClosedOutcome deal={deal} open={open} />
    <span className="dc-figure">{deal.revenueYear === null ? 'No figure yet' : `${moneyFull(deal.revenueYear, deal.currency)} a year`}</span>
    {deal.mayUndo && deal.undoUntil && Date.parse(deal.undoUntil) > Date.now() ? <ActionButton className="hx-link" type="button" disabled={busy} onClick={() => {
      setBusy(true); setWrong(null);
      void closeApi.undo(store, deal.id, deal.outcome).then(changed).catch((e: unknown) => { setBusy(false); setWrong(e instanceof Error ? e.message : 'Not saved. Try again.'); });
    }}>Undo</ActionButton> : <ActionButton reading type="button" className="hx-link" onClick={() => open(`/deals/${encodeURIComponent(deal.id)}`)}>To reopen it, open the deal</ActionButton>}
    {wrong ? <p role="alert" className="lv-said">{wrong}</p> : null}
  </div>;
}

export function ClosedDeals({ store, open, stamp, changed }: { store: string; open: (path: string) => void; stamp: number; changed: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [summary, setSummary] = useState<ClosedSummary | null>(null);
  const [deals, setDeals] = useState<ClosedDeal[] | null>(null);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [wrong, setWrong] = useState(false);
  const [listWrong, setListWrong] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setWrong(false);
    void closeApi.summary(store, !expanded).then((s) => { if (active) setSummary(s); }).catch(() => { if (active) setWrong(true); });
    return () => { active = false; };
  }, [store, expanded, stamp, retry]);
  useEffect(() => {
    if (!expanded) return;
    let active = true;
    setDeals(null); setListWrong(false);
    void closeApi.list(store, month).then((r) => { if (active) setDeals(r.deals); }).catch(() => { if (active) setListWrong(true); });
    return () => { active = false; };
  }, [store, expanded, month, stamp, retry]);
  const totals = summary?.totals;
  const maximum = Math.max(1, ...(summary?.months.flatMap((m) => [Number(m.wonRevenueYear), Number(m.lostRevenueYear)]) ?? []));
  const failed = <p className="lv-said" role="alert">The closed deals did not load. Nothing was changed. <ActionButton reading className="hx-link" type="button" onClick={() => setRetry((n) => n + 1)}>Try again</ActionButton></p>;
  return <section className="dc-closed" aria-label="Closed deals">
    <ActionButton reading className="dc-summary" type="button" aria-expanded={expanded} aria-controls="closed-body" onClick={() => { setExpanded(!expanded); if (!expanded) setMonth(new Date().toISOString().slice(0, 7)); }}>
      <span>Closed{totals ? `: ${totals.wonCount} won, ${moneyFull(totals.wonRevenueYear, summary!.homeCurrency)} a year; ${totals.lostCount} lost; over 12 months` : wrong ? '' : ': Reading the closed deals'}</span><span aria-hidden="true">{expanded ? '▾' : '›'}</span>
    </ActionButton>
    {expanded ? <div id="closed-body" className="dc-body">
      {wrong ? failed : !summary || summary.months.length !== 12 ? <p className="lv-meta" role="status">Reading the closed deals</p>
        : <>
          {summary.totals.wonCount + summary.totals.lostCount === 0 ? <p className="lv-meta">{CLOSED_EMPTY}</p> : null}
          <div className="dc-chart" role="group" aria-label="Revenue won and lost per month, last 12 months">
            {summary.months.map((m) => <ActionButton reading className="dc-month" type="button" key={m.month} aria-pressed={month === m.month}
              aria-label={`${monthWords(m.month)}: ${moneyFull(m.wonRevenueYear, summary.homeCurrency)} won, ${moneyFull(m.lostRevenueYear, summary.homeCurrency)} lost`} onClick={() => setMonth(m.month)}>
              <span className="dc-bars"><i className="won" style={{ height: `${Number(m.wonRevenueYear) / maximum * 100}%` }} /><i className="lost" style={{ height: `${Number(m.lostRevenueYear) / maximum * 100}%` }} /></span>
              <span>{monthWords(m.month, true)}</span>
            </ActionButton>)}
          </div>
          <div className="dc-key"><span><i className="won" />Won, revenue a year</span><span><i className="lost" />Lost, revenue a year</span><span>Counted from deals closed in Cobrain</span></div>
        </>}
      <div className="dc-list">
        <h3>{month === 'all' ? 'Every closed deal' : monthWords(month)}{month !== 'all' && summary?.months.find((x) => x.month === month) ? (() => {
          const m = summary.months.find((x) => x.month === month)!;
          return `: ${moneyFull(m.wonRevenueYear, summary.homeCurrency)} won, ${moneyFull(m.lostRevenueYear, summary.homeCurrency)} lost`;
        })() : ''}</h3>
        {listWrong ? failed : deals === null ? <p className="lv-meta" role="status">Reading the closed deals</p>
          : deals.length === 0 ? <p className="lv-meta">{month === 'all' ? CLOSED_EMPTY : `No deal was won or lost in ${monthWords(month, false).split(' ')[0]}.`}</p>
          : deals.map((d) => <ClosedRow key={d.id} deal={d} store={store} open={open} changed={() => { setRetry((n) => n + 1); changed(); }} />)}
        <ActionButton reading type="button" className="hx-link" onClick={() => setMonth('all')}>Every closed deal</ActionButton>
      </div>
    </div> : null}
  </section>;
}

/** Closed deals leave heat; their own page reads the state and offers the same week's Undo. */
export function DealCloseState({ store, id, open, changed, stamp }: { store: string; id: string; open: (path: string) => void; changed: () => void; stamp: unknown }) {
  const [deal, setDeal] = useState<ClosedDeal | null>(null);
  const [wrong, setWrong] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setWrong(false);
    void closeApi.state(store, id).then((s) => { if (active) setDeal(s.closed); }).catch(() => { if (active) setWrong(true); });
    return () => { active = false; };
  }, [store, id, stamp, retry]);
  return wrong ? <p className="lv-said" role="alert">The closed deals did not load. Nothing was changed. <ActionButton reading className="hx-link" onClick={() => setRetry((n) => n + 1)}>Try again</ActionButton></p>
    : deal ? <ClosedRow deal={deal} store={store} open={open} changed={() => { setRetry((n) => n + 1); changed(); }} /> : null;
}
